"use client"

import * as React from "react"
import Box from "@mui/material/Box"
import Divider from "@mui/material/Divider"
import TextField from "@mui/material/TextField"
import { SvgIcon } from "@mui/material"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Snackbar from "@mui/material/Snackbar"
import { useDelayedSignIn } from "@/hooks/useDelayedSignIn"
import { useSearchParams } from "next/navigation"
import { useGtmEvent } from "@/hooks/useGTMEvents"

export default function SignInForm() {
  const emailFieldError = "Veuillez saisir un email valide"
  const searchParams = useSearchParams()
  const rawRedirectTo = searchParams.get("redirectTo")
  const redirectToUrl: string | undefined = rawRedirectTo
    ? rawRedirectTo.startsWith("/")
      ? rawRedirectTo
      : `/${rawRedirectTo}`
    : undefined

  const { loading, error, signInWithDelay, setError } = useDelayedSignIn()
  const {
    loading: googleLoading,
    error: googleError,
    signInWithDelay: signInWithDelayGoogle,
    setError: setGoogleError,
  } = useDelayedSignIn()

  const pushGtmEvent = useGtmEvent()
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    pushGtmEvent({ event: "sign_in_email_click" })
    const data = new FormData(event.currentTarget)
    const email = data.get("email") as string
    await signInWithDelay("resend", { email, redirectTo: redirectToUrl })
  }

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: 2,
        }}
      >
        <TextField
          required
          fullWidth
          id="email"
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          inputProps={{
            onInvalid: (e) => {
              ;(e.target as HTMLInputElement).setCustomValidity(emailFieldError)
            },
            onInput: (e) => {
              ;(e.target as HTMLInputElement).setCustomValidity("")
            },
          }}
        />
        <LoadingButton
          loading={loading}
          disableRipple
          type="submit"
          fullWidth
          variant="contained"
        >
          Se connecter avec mon email
        </LoadingButton>
      </Box>

      <Divider>ou</Divider>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <LoadingButton
          loading={googleLoading}
          disableRipple
          fullWidth
          variant="outlined"
          onClick={() => {
            pushGtmEvent({ event: "sign_in_google_click" })
            signInWithDelayGoogle("google", {
              redirectTo: redirectToUrl,
            })
          }}
          startIcon={<GoogleIcon />}
        >
          Se connecter avec Google
        </LoadingButton>
      </Box>
      <Snackbar
        open={!!(error || googleError)}
        autoHideDuration={6000}
        onClose={() => {
          setError(undefined)
          setGoogleError(undefined)
        }}
      >
        <Alert
          onClose={() => {
            setError(undefined)
            setGoogleError(undefined)
          }}
          severity="error"
          sx={{ width: "100%" }}
        >
          Une erreur nous a empeché de vous connecter. Veuillez réessayer.
        </Alert>
      </Snackbar>
    </>
  )
}

function GoogleIcon() {
  return (
    <SvgIcon>
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M15.68 8.18182C15.68 7.61455 15.6291 7.06909 15.5345 6.54545H8V9.64364H12.3055C12.1164 10.64 11.5491 11.4836 10.6982 12.0509V14.0655H13.2945C14.8073 12.6691 15.68 10.6182 15.68 8.18182Z"
          fill="#4285F4"
        />
        <path
          d="M8 16C10.16 16 11.9709 15.2873 13.2945 14.0655L10.6982 12.0509C9.98545 12.5309 9.07636 12.8218 8 12.8218C5.92 12.8218 4.15273 11.4182 3.52 9.52727H0.858182V11.5927C2.17455 14.2036 4.87273 16 8 16Z"
          fill="#34A853"
        />
        <path
          d="M3.52 9.52C3.36 9.04 3.26545 8.53091 3.26545 8C3.26545 7.46909 3.36 6.96 3.52 6.48V4.41455H0.858182C0.312727 5.49091 0 6.70545 0 8C0 9.29455 0.312727 10.5091 0.858182 11.5855L2.93091 9.97091L3.52 9.52Z"
          fill="#FBBC05"
        />
        <path
          d="M8 3.18545C9.17818 3.18545 10.2255 3.59273 11.0618 4.37818L13.3527 2.08727C11.9636 0.792727 10.16 0 8 0C4.87273 0 2.17455 1.79636 0.858182 4.41455L3.52 6.48C4.15273 4.58909 5.92 3.18545 8 3.18545Z"
          fill="#EA4335"
        />
      </svg>
    </SvgIcon>
  )
}
